import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ProfileData } from '../types/profile.types';
import { AnalyticsEvents } from '../services/analytics';
import { 
  participationModalities, 
  eventProgramParticipation, 
  catalogSections, 
  thematicAxes
} from '../data/profileData';

/**
 * Genera un PDF del perfil institucional y retorna el documento
 * @param returnBlob Si es true, retorna el blob en lugar de descargar
 */
const buildProfilePDF = async (
  profileData: ProfileData,
  onProgress?: (progress: number) => void
): Promise<jsPDF> => {
  try {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Funciones helper para convertir valores codificados a nombres legibles
    const getThematicAxisLabel = (value: string): string => {
      return thematicAxes.find(t => t.value === value)?.label || value;
    };

    const getParticipationModalityLabel = (value: string): string => {
      return participationModalities.find(m => m.value === value)?.label || value;
    };

    // Funciones helper no utilizadas actualmente (comentadas para evitar warnings de ESLint)
    // const getEventProgramParticipationLabel = (value: string): string => {
    //   return eventProgramParticipation.find(p => p.value === value)?.label || value;
    // };

    // const getCatalogSectionLabel = (value: any): string => {
    //   const sectionId = typeof value === 'object' && value !== null ? (value as any).id || value : value;
    //   return catalogSections.find(s => s.value === sectionId)?.label || String(sectionId);
    // };

    const getCurrentStatusLabel = (value: string): string => {
      const statusMap: Record<string, string> = {
        'en_diseno': 'En Diseño',
        'piloto': 'Piloto',
        'implementado': 'Implementado',
        'escalando': 'Escalando'
      };
      return statusMap[value] || value;
    };

    // Función helper no utilizada actualmente (comentada para evitar warnings de ESLint)
    // const getSolutionTypeLabel = (value: string): string => {
    //   const typeMap: Record<string, string> = {
    //     'software': 'Software',
    //     'hardware': 'Hardware',
    //     'infraestructura': 'Infraestructura',
    //     'servicio': 'Servicio',
    //     'metodologico': 'Metodológico',
    //     'hibrido': 'Híbrido',
    //     'otro': 'Otro'
    //   };
    //   return typeMap[value] || value;
    // };

    const getPolicyTypeLabel = (value: string): string => {
      const typeMap: Record<string, string> = {
        'programa': 'Programa',
        'piloto': 'Piloto',
        'politica': 'Política',
        'estrategia': 'Estrategia',
        'servicio': 'Servicio',
        'infraestructura': 'Infraestructura',
        'otro': 'Otro'
      };
      return typeMap[value] || value;
    };

    const getEstimatedBeneficiariesLabel = (value: string): string => {
      const beneficiariesMap: Record<string, string> = {
        'hasta_500': 'Hasta 500',
        '500_5000': '500 - 5,000',
        '5000_50000': '5,000 - 50,000',
        'mas_50000': 'Más de 50,000',
        'no_aplica': 'No aplica'
      };
      return beneficiariesMap[value] || value;
    };

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - (margin * 2);
    let yPosition = margin;

    // Iniciar progreso
    if (onProgress) onProgress(0);

    // Función helper para convertir hex a RGB
    const hexToRgb = (hex: string): [number, number, number] => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? [
            parseInt(result[1], 16),
            parseInt(result[2], 16),
            parseInt(result[3], 16)
          ]
        : [0, 0, 0];
    };

    // Función helper para agregar texto con wrap
    const addText = (text: string, fontSize: number, isBold: boolean = false, color: string = '#000000') => {
      if (!text) return;

      doc.setFontSize(fontSize);
      const rgb = hexToRgb(color);
      doc.setTextColor(rgb[0], rgb[1], rgb[2]);
      if (isBold) {
        doc.setFont('helvetica', 'bold');
      } else {
        doc.setFont('helvetica', 'normal');
      }

      // Dividir texto en líneas que quepan en el ancho máximo
      let lines: string[] = [];
      const textStr = String(text);

      // Detectar indent (espacios al inicio)
      const indentMatch = textStr.match(/^(\s+)/);
      const indent = indentMatch ? indentMatch[1] : '';
      const indentWidth = indent ? doc.getTextWidth(indent) : 0;
      const textWithoutIndent = textStr.replace(/^\s+/, '');
      const textX = margin + indentWidth;
      const maxWidth = pageWidth - (margin * 2) - indentWidth; // Reducir ancho disponible por el indent

      // Intentar usar splitTextToSize si está disponible
      if (typeof (doc as any).splitTextToSize === 'function') {
        try {
          lines = (doc as any).splitTextToSize(textWithoutIndent, maxWidth);
        } catch (e) {
          lines = [];
        }
      }

      // Si no funcionó, usar método manual
      if (lines.length === 0) {
        const words = textWithoutIndent.split(' ');
        let currentLine = '';

        words.forEach((word) => {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          let textWidth = 0;

          try {
            textWidth = doc.getTextWidth(testLine);
          } catch (e) {
            // Si getTextWidth falla, estimar basado en caracteres
            textWidth = testLine.length * (fontSize * 0.5);
          }

          if (textWidth > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        });

        if (currentLine) {
          lines.push(currentLine);
        }
      }

      if (yPosition + (lines.length * fontSize * 0.4) > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }

      lines.forEach((line: string, lineIndex: number) => {
        // Si la línea contiene ":", poner el label (hasta ":") en negrita
        if (line.includes(':')) {
          const colonIndex = line.indexOf(':');
          const label = line.substring(0, colonIndex + 1); // Incluye ":"
          const value = line.substring(colonIndex + 1).trim();

          // Escribir label en negrita (con indent en todas las líneas)
          doc.setFont('helvetica', 'bold');
          const labelWithIndent = indent ? indent + label : label;
          doc.text(labelWithIndent, margin, yPosition);

          // Calcular posición para el valor (con espacio después de ":")
          const labelWidth = doc.getTextWidth(labelWithIndent);
          const spaceWidth = doc.getTextWidth(' '); // Ancho de un espacio
          const valueX = margin + labelWidth + spaceWidth;

          // Escribir valor en normal
          if (value) {
            doc.setFont('helvetica', 'normal');
            doc.text(value, valueX, yPosition);
          }
        } else {
          // Comportamiento normal (sin ":")
          if (isBold) {
            doc.setFont('helvetica', 'bold');
          } else {
            doc.setFont('helvetica', 'normal');
          }
          // Aplicar indent a todas las líneas
          const lineWithIndent = indent ? indent + line : line;
          doc.text(lineWithIndent, margin, yPosition);
        }
        yPosition += fontSize * 0.4;
      });

      yPosition += 3; // Espacio entre elementos
    };

    // Función helper para convertir código de país a nombre completo
    const getCountryName = (countryCode: string): string => {
      const countries: Record<string, string> = {
        'AR': 'Argentina', 'BR': 'Brasil', 'CL': 'Chile', 'CO': 'Colombia', 'MX': 'México',
        'PE': 'Perú', 'UY': 'Uruguay', 'VE': 'Venezuela', 'BO': 'Bolivia', 'EC': 'Ecuador',
        'PY': 'Paraguay', 'CR': 'Costa Rica', 'PA': 'Panamá', 'GT': 'Guatemala', 'HN': 'Honduras',
        'SV': 'El Salvador', 'NI': 'Nicaragua', 'CU': 'Cuba', 'DO': 'República Dominicana',
        'HT': 'Haití', 'JM': 'Jamaica', 'TT': 'Trinidad y Tobago', 'US': 'Estados Unidos',
        'CA': 'Canadá', 'ES': 'España', 'IT': 'Italia', 'FR': 'Francia', 'DE': 'Alemania',
        'GB': 'Reino Unido', 'PT': 'Portugal', 'NL': 'Países Bajos', 'BE': 'Bélgica', 'CH': 'Suiza',
        'AT': 'Austria', 'SE': 'Suecia', 'NO': 'Noruega', 'DK': 'Dinamarca', 'FI': 'Finlandia',
        'PL': 'Polonia', 'CZ': 'República Checa', 'HU': 'Hungría', 'RO': 'Rumania', 'BG': 'Bulgaria',
        'GR': 'Grecia', 'RU': 'Rusia', 'UA': 'Ucrania', 'IE': 'Irlanda', 'IS': 'Islandia',
        'CN': 'China', 'JP': 'Japón', 'KR': 'Corea del Sur', 'IN': 'India', 'TH': 'Tailandia',
        'SG': 'Singapur', 'MY': 'Malasia', 'ID': 'Indonesia', 'PH': 'Filipinas', 'VN': 'Vietnam',
        'TW': 'Taiwán', 'HK': 'Hong Kong', 'BD': 'Bangladesh', 'PK': 'Pakistán', 'LK': 'Sri Lanka',
        'NP': 'Nepal', 'MM': 'Myanmar', 'KH': 'Camboya', 'LA': 'Laos', 'MN': 'Mongolia',
        'AU': 'Australia', 'NZ': 'Nueva Zelanda', 'FJ': 'Fiyi', 'PG': 'Papúa Nueva Guinea',
        'NC': 'Nueva Caledonia', 'ZA': 'Sudáfrica', 'EG': 'Egipto', 'NG': 'Nigeria', 'KE': 'Kenia',
        'MA': 'Marruecos', 'TN': 'Túnez', 'DZ': 'Argelia', 'GH': 'Ghana', 'ET': 'Etiopía',
        'UG': 'Uganda', 'TZ': 'Tanzania', 'ZW': 'Zimbabue', 'BW': 'Botsuana', 'NA': 'Namibia',
        'ZM': 'Zambia', 'MW': 'Malaui', 'MZ': 'Mozambique', 'MG': 'Madagascar', 'MU': 'Mauricio',
        'SC': 'Seychelles', 'SA': 'Arabia Saudí', 'AE': 'Emiratos Árabes Unidos', 'QA': 'Catar',
        'KW': 'Kuwait', 'BH': 'Baréin', 'OM': 'Omán', 'JO': 'Jordania', 'LB': 'Líbano',
        'SY': 'Siria', 'IQ': 'Irak', 'IR': 'Irán', 'IL': 'Israel', 'TR': 'Turquía'
      };
      return countries[countryCode] || countryCode;
    };

    // Función helper para agregar título de sección
    const addSectionTitle = (title: string) => {
      if (yPosition > margin + 10) {
        yPosition += 10; // Aumentar espacio antes de nueva sección
      }
      addText(title, 14, true, '#CE2B15');
      yPosition += 3; // Aumentar espacio después del título
    };

    // Función helper para agregar imagen desde URL con altura fija y width adaptable
    // Función helper para convertir URLs de WordPress a URLs del proxy
    const getProxyUrl = (originalUrl: string): string => {
      const wordpressDomain = 'smartcityexposantiagodelestero.com';
      if (!originalUrl || !originalUrl.includes(wordpressDomain)) {
        return originalUrl;
      }
      let proxyBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;
      proxyBaseUrl = proxyBaseUrl.replace(/\/api\/?$/, '');
      return `${proxyBaseUrl}/api/proxy-image?url=${encodeURIComponent(originalUrl)}`;
    };

    const addImageFromUrl = async (url: string, fixedHeight: number) => {
      try {
        // Usar proxy si la URL es de WordPress
        const imageUrl = getProxyUrl(url);

        // Cargar la imagen para obtener sus dimensiones
        const img = new Image();
        img.crossOrigin = 'anonymous';

        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageUrl;
        });

        // Calcular width manteniendo el aspect ratio
        const aspectRatio = img.width / img.height;
        const imgWidth = fixedHeight * aspectRatio;

        if (yPosition + fixedHeight > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }

        // Usar la URL del proxy también para agregar al PDF
        doc.addImage(imageUrl, 'PNG', margin, yPosition, imgWidth, fixedHeight);
        yPosition += fixedHeight + 5;
      } catch (error) {

      }
    };

    // ========== PERFIL INSTITUCIONAL ==========

    // Logos del header (SCESDE y MOOD) - uno al lado del otro
    const logoSCESDEUrl = 'https://smartcityexposantiagodelestero.com/wp-content/uploads/2025/03/Logo-SCESDE26-Negro-e1751508867641-1536x213.png';
    const logoMOODUrl = 'https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/logo-mood-color-e1768878022934.png';

    try {
      // Cargar ambos logos
      const imgSCESDE = new Image();
      imgSCESDE.crossOrigin = 'anonymous';
      const imgMOOD = new Image();
      imgMOOD.crossOrigin = 'anonymous';

      const [scesdeLoaded, moodLoaded] = await Promise.all([
        new Promise((resolve, reject) => {
          imgSCESDE.onload = resolve;
          imgSCESDE.onerror = reject;
          imgSCESDE.src = getProxyUrl(logoSCESDEUrl);
        }),
        new Promise((resolve, reject) => {
          imgMOOD.onload = resolve;
          imgMOOD.onerror = reject;
          imgMOOD.src = getProxyUrl(logoMOODUrl);
        })
      ]);

      // Altura fija para ambos logos (usar la misma altura para alineación vertical)
      const logoHeight = 15;
      const spacing = 20; // Espacio entre logos

      // Calcular anchos manteniendo aspect ratio
      const scesdeAspectRatio = imgSCESDE.width / imgSCESDE.height;
      const moodAspectRatio = imgMOOD.width / imgMOOD.height;
      const scesdeWidth = logoHeight * scesdeAspectRatio;
      const moodWidth = logoHeight * moodAspectRatio;

      // Calcular altura real de cada logo escalado (debería ser logoHeight, pero verificamos)
      const scesdeScaledHeight = logoHeight;
      const moodScaledHeight = logoHeight;

      // Usar la altura máxima para alinear verticalmente ambos logos
      const maxLogoHeight = Math.max(scesdeScaledHeight, moodScaledHeight, logoHeight);

      // Calcular posición para centrar ambos logos horizontalmente
      const totalWidth = scesdeWidth + spacing + moodWidth;
      const startX = (pageWidth - totalWidth) / 2;

      if (yPosition + maxLogoHeight > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }

      // Ambos logos usan la misma posición Y para alineación vertical perfecta
      const logosY = yPosition;

      // Agregar logo SCESDE (alineado verticalmente)
      doc.addImage(getProxyUrl(logoSCESDEUrl), 'PNG', startX, logosY, scesdeWidth, logoHeight);

      // Agregar logo MOOD (alineado verticalmente en la misma posición Y)
      doc.addImage(getProxyUrl(logoMOODUrl), 'PNG', startX + scesdeWidth + spacing, logosY, moodWidth, logoHeight);

      // Aumentar significativamente el margen superior del título
      yPosition += maxLogoHeight + 20; // Margen considerable antes del título
    } catch (error) {

      // Continuar sin logos si hay error
    }

    // Título de sección principal
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const titleRgb = hexToRgb('#CE2B15');
    doc.setTextColor(titleRgb[0], titleRgb[1], titleRgb[2]);
    doc.text('PERFIL INSTITUCIONAL', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8;

    // Verificar si wantsToManageProfile es false (perfil personal desactivado)
    const wantsToManageProfile = profileData.contactInfo?.wantsToManageProfile || false;

    // Si wantsToManageProfile es false, agregar campos del perfil personal al inicio
    if (!wantsToManageProfile && profileData.contactInfo) {
      addSectionTitle('Información de Contacto');

      if (profileData.contactInfo.firstName || profileData.contactInfo.lastName) {
        const fullName = `${profileData.contactInfo.firstName || ''} ${profileData.contactInfo.lastName || ''}`.trim();
        if (fullName) {
          addText(`Nombre: ${fullName}`, 10);
        }
      }
      if (profileData.contactInfo.position?.trim()) {
        addText(`Cargo: ${profileData.contactInfo.position}`, 10);
      }
      if (profileData.contactInfo.email?.trim()) {
        addText(`Email: ${profileData.contactInfo.email}`, 10);
      }
      if (profileData.contactInfo.phone || profileData.contactInfo.numeroTelefono) {
        const phone = profileData.contactInfo.phone || 
          (profileData.contactInfo.codigoPais && profileData.contactInfo.numeroTelefono 
            ? `${profileData.contactInfo.codigoPais} ${profileData.contactInfo.numeroTelefono}` 
            : '');
        if (phone?.trim()) {
          addText(`Teléfono: ${phone}`, 10);
        }
      }
      if (profileData.basicInfo?.organizationName?.trim()) {
        addText(`Organización: ${profileData.basicInfo.organizationName}`, 10);
      }
      if (profileData.basicInfo?.organizationType?.trim()) {
        addText(`Tipo de organización: ${profileData.basicInfo.organizationType}`, 10);
      }
      if (profileData.contactInfo.area?.trim()) {
        addText(`Área: ${profileData.contactInfo.area}`, 10);
      }
      if (profileData.contactInfo.subarea?.trim()) {
        addText(`Subárea: ${profileData.contactInfo.subarea}`, 10);
      }
      if (profileData.basicInfo?.website?.trim()) {
        addText(`Sitio web: ${profileData.basicInfo.website}`, 10);
      }
      if (profileData.contactInfo.thematicAxes && Array.isArray(profileData.contactInfo.thematicAxes) && profileData.contactInfo.thematicAxes.length > 0) {
        // Convertir ejes temáticos a nombres legibles
        const thematicAxesLabels = profileData.contactInfo.thematicAxes.map(axis => {
          const axisLabel = thematicAxes.find(t => t.value === axis)?.label || axis;
          return axisLabel;
        });
        addText(`Ejes Temáticos de Interés: ${thematicAxesLabels.join('; ')}`, 10);
      }

      yPosition += 5; // Espacio antes de continuar con el perfil institucional
    }

    // Información de la Organización
    if (profileData.basicInfo) {
      const hasOrgInfo = profileData.basicInfo.country?.trim() ||
        (profileData.basicInfo.industries && Array.isArray(profileData.basicInfo.industries) && profileData.basicInfo.industries.length > 0) ||
        (profileData.basicInfo.thematicAxes && Array.isArray(profileData.basicInfo.thematicAxes) && profileData.basicInfo.thematicAxes.length > 0) ||
        (profileData.basicInfo.eventProgramParticipation && Array.isArray(profileData.basicInfo.eventProgramParticipation) && profileData.basicInfo.eventProgramParticipation.length > 0) ||
        (profileData.basicInfo.socialNetworks && (
          profileData.basicInfo.socialNetworks.facebook?.trim() ||
          profileData.basicInfo.socialNetworks.twitter?.trim() ||
          profileData.basicInfo.socialNetworks.linkedin?.trim() ||
          profileData.basicInfo.socialNetworks.instagram?.trim() ||
          profileData.basicInfo.socialNetworks.youtube?.trim() ||
          profileData.basicInfo.socialNetworks.other?.trim()
        ));

      if (hasOrgInfo) {
        addSectionTitle('Información de la Organización');

        // Logo (si existe) - altura fija de 25mm (igual que el resto de imágenes/logos), width adaptable
        if (profileData.basicInfo?.logo && typeof profileData.basicInfo.logo === 'string') {
          try {
            await addImageFromUrl(profileData.basicInfo.logo, 25);
          } catch (error) {
          }
        }

        // Campos del perfil personal que se muestran en el perfil institucional
        if (profileData.basicInfo?.organizationName?.trim()) {
          addText(`Nombre de la organización: ${profileData.basicInfo.organizationName}`, 10);
        }
        if (profileData.basicInfo?.organizationType?.trim()) {
          addText(`Tipo de organización: ${profileData.basicInfo.organizationType}`, 10);
        }
        if (profileData.basicInfo.industries && Array.isArray(profileData.basicInfo.industries) && profileData.basicInfo.industries.length > 0) {
          addText(`Industrias: ${profileData.basicInfo.industries.join('; ')}`, 10);
        }
        if (profileData.basicInfo?.website?.trim()) {
          addText(`Sitio web: ${profileData.basicInfo.website}`, 10);
        }

        if (profileData.basicInfo.country?.trim()) {
          const countryName = getCountryName(profileData.basicInfo.country);
          addText(`País: ${countryName}`, 10);
        }
        // Descripción Institucional como campo dentro de Información de la Organización
        if (profileData.institutionalDescription?.description?.trim()) {
          addText(`Descripción institucional: ${profileData.institutionalDescription.description}`, 10);
        }
        if (profileData.basicInfo.thematicAxes && Array.isArray(profileData.basicInfo.thematicAxes) && profileData.basicInfo.thematicAxes.length > 0) {
          // Convertir ejes temáticos a nombres legibles
          const thematicAxesLabels = profileData.basicInfo.thematicAxes.map(axis => {
            const axisLabel = thematicAxes.find(t => t.value === axis)?.label || axis;
            return axisLabel;
          });
          addText(`Ejes Temáticos de la Organización: ${thematicAxesLabels.join('; ')}`, 10);
        }

        // Redes sociales
        if (profileData.basicInfo.socialNetworks) {
          const socialNetworks = profileData.basicInfo.socialNetworks;
          const socialEntries: string[] = [];
          if (socialNetworks.facebook?.trim()) socialEntries.push(`Facebook: ${socialNetworks.facebook}`);
          if (socialNetworks.twitter?.trim()) socialEntries.push(`Twitter: ${socialNetworks.twitter}`);
          if (socialNetworks.linkedin?.trim()) socialEntries.push(`LinkedIn: ${socialNetworks.linkedin}`);
          if (socialNetworks.instagram?.trim()) socialEntries.push(`Instagram: ${socialNetworks.instagram}`);
          if (socialNetworks.youtube?.trim()) {
            const youtubeValue = socialNetworks.youtube.trim();
            socialEntries.push(`YouTube: ${youtubeValue}`);
          }
          if (socialNetworks.other?.trim()) socialEntries.push(`Otro: ${socialNetworks.other}`);
          if (socialEntries.length > 0) {
            addText(`Redes Sociales:`, 10, true);
            socialEntries.forEach(social => {
              // Agregar item (viñeta) y sangría
              addText(`  • ${social}`, 9);
            });
          }
        }
      }
    }

    if (onProgress) onProgress(20);

    // ========== EL EVENTO ==========

    // Modalidades de Participación - SIEMPRE mostrar esta sección si hay datos
    // Normalizar participationModalities si viene como string o necesita parseo
    let normalizedParticipationModalities: any[] = [];
    if (profileData.participationModalities) {
      if (Array.isArray(profileData.participationModalities)) {
        normalizedParticipationModalities = profileData.participationModalities;
      } else if (typeof profileData.participationModalities === 'string') {
        try {
          const parsed = JSON.parse(profileData.participationModalities);
          normalizedParticipationModalities = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          normalizedParticipationModalities = [];
        }
      }
    }

    // Filtrar elementos vacíos, null, undefined, strings vacíos y caracteres inválidos
    normalizedParticipationModalities = normalizedParticipationModalities.filter(mod => {
      if (mod === null || mod === undefined) return false;
      const modStr = String(mod).trim();
      // Filtrar strings vacíos, caracteres inválidos, valores falsy y solo espacios/puntuación
      if (modStr === '' || 
          modStr === '[' || 
          modStr === ']' || 
          modStr === ';' || 
          modStr === ',' || 
          modStr === 'null' || 
          modStr === 'undefined' ||
          modStr === 'false' ||
          modStr.length === 0 ||
          /^[\s[\];,]+$/.test(modStr)) { // Solo espacios, corchetes, punto y coma o comas
        return false;
      }
      return true;
    });

    // Verificar si hay datos de modalidades de participación
    const hasModalities = normalizedParticipationModalities.length > 0;
    const hasAgreedModality = profileData.agreedModality && typeof profileData.agreedModality === 'string' && profileData.agreedModality.trim().length > 0;
    const hasObservations = profileData.modalityObservations && typeof profileData.modalityObservations === 'string' && profileData.modalityObservations.trim().length > 0;

    const hasParticipationData = Boolean(hasModalities || hasAgreedModality || hasObservations);

    if (hasParticipationData) {
      addSectionTitle('Modalidades de Participación');

      // Participación en el programa del evento (antes de Modalidades de interés)
      if (profileData.basicInfo.eventProgramParticipation && Array.isArray(profileData.basicInfo.eventProgramParticipation) && profileData.basicInfo.eventProgramParticipation.length > 0) {
        // Convertir participación en programa a nombres legibles
        const participationLabels = profileData.basicInfo.eventProgramParticipation.map(part => {
          const partLabel = eventProgramParticipation.find(p => p.value === part)?.label || part;
          return partLabel;
        });
        addText(`Participación en el programa del evento: ${participationLabels.join('; ')}`, 10);
      }

      if (hasModalities) {
        // Convertir modalidades a nombres legibles y filtrar labels vacíos o inválidos
        const modalityLabels = normalizedParticipationModalities
          .map(mod => {
            const label = getParticipationModalityLabel(mod);
            return label && label.trim() !== '' ? label.trim() : null;
          })
          .filter((label): label is string => {
            if (!label) return false;
            const trimmed = label.trim();
            // Filtrar labels vacíos, null, undefined, o que solo contengan caracteres especiales
            return trimmed !== '' && 
                   trimmed !== 'null' && 
                   trimmed !== 'undefined' && 
                   !/^[\s[\];,]+$/.test(trimmed) &&
                   trimmed.length > 0;
          });

        if (modalityLabels.length > 0) {
          addText(`Modalidades de interés: ${modalityLabels.join('; ')}`, 10);
        } else {
        }
      }

      if (hasAgreedModality && profileData.agreedModality) {
        // Convertir modalidad acordada a nombre legible
        const agreedModLabel = getParticipationModalityLabel(profileData.agreedModality);
        addText(`Modalidad Acordada: ${agreedModLabel}`, 10, true);
      }

      if (hasObservations) {
        addText(`Observaciones: ${profileData.modalityObservations}`, 10);
      }
    } else {
    }

    // Función para capturar la Zona Expo visualmente
    const captureExpoZone = async (): Promise<boolean> => {

      // Guardar pestañas activas actuales para restaurarlas después (fuera del try para que estén disponibles en catch)
      const activeMainTabButton = document.querySelector('.tab-button.active') as HTMLElement;
      const activeSubTabButton = document.querySelector('.sub-tab-button.active') as HTMLElement;
      const savedMainTabText = activeMainTabButton?.textContent?.trim() || '';
      const savedSubTabText = activeSubTabButton?.textContent?.trim() || '';

      // Guardar estado del sidebar antes de la captura
      const sidebarElement = document.querySelector('.sidebar') as HTMLElement;
      const wasSidebarOpen = sidebarElement?.classList.contains('open') || false;

      // Declarar tempContainer fuera del try para que esté disponible en catch
      let tempContainer: HTMLElement | null = null;

      try {
        // PRIMERO: Activar las pestañas correctas para que el componente se renderice

        // Buscar y activar la pestaña principal "Perfil Institucional"
        const allTabButtons = Array.from(document.querySelectorAll('.tab-button'));

        allTabButtons.forEach((btn, idx) => {

        });

        const institutionalTabButton = allTabButtons.find(btn => {
          const label = btn.textContent?.trim() || '';
          const lowerLabel = label.toLowerCase();
          return lowerLabel.includes('institucional') || lowerLabel.includes('perfil institucional');
        }) as HTMLElement;

        if (institutionalTabButton) {

          institutionalTabButton.click();
          await new Promise(resolve => setTimeout(resolve, 1000)); // Esperar renderizado
        } else {

          // Intentar buscar cualquier pestaña que no sea "Perfil Personal"
          const nonPersonalTab = allTabButtons.find(btn => {
            const label = btn.textContent?.trim() || '';
            const lowerLabel = label.toLowerCase();
            return !lowerLabel.includes('personal');
          }) as HTMLElement;
          if (nonPersonalTab) {

            nonPersonalTab.click();
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        // Buscar y activar la subpestaña "Zona Expo"
        await new Promise(resolve => setTimeout(resolve, 500)); // Esperar a que se rendericen las subpestañas
        const allSubTabButtons = Array.from(document.querySelectorAll('.sub-tab-button'));

        allSubTabButtons.forEach((btn, idx) => {

        });

        const expoSubTabButton = allSubTabButtons.find(btn => {
          const label = btn.textContent?.trim() || '';
          const lowerLabel = label.toLowerCase();
          return lowerLabel.includes('zona expo') || lowerLabel.includes('expo');
        }) as HTMLElement;

        if (expoSubTabButton) {

          expoSubTabButton.click();
          await new Promise(resolve => setTimeout(resolve, 2000)); // Aumentar tiempo de espera para renderizado completo
        } else {

        }

        // Buscar el elemento de la sección Expo Zone - intentar múltiples selectores
        // Esperar más tiempo para que el DOM se actualice completamente después de activar las pestañas
        await new Promise(resolve => setTimeout(resolve, 1500));

        let expoZoneElement: HTMLElement | null = null;

        // Buscar el contenedor principal .expo-zone-section
        const expoZoneSection = document.querySelector('.expo-zone-section') as HTMLElement;

        if (expoZoneSection) {
          // Cerrar el sidebar durante la captura
          if (sidebarElement) {
            sidebarElement.classList.remove('open');
            sidebarElement.classList.add('closed');

          }

          // Actualizar la clase del form-content-area para que el contenido se adapte
          const formContentArea = document.querySelector('.form-content-area') as HTMLElement;
          if (formContentArea) {
            formContentArea.classList.remove('sidebar-open');
            formContentArea.classList.add('sidebar-closed');

          }

          // Esperar a que el sidebar se cierre y el layout se recalcule
          await new Promise(resolve => setTimeout(resolve, 300));

          // Forzar un reflow para asegurar que el layout se haya recalculado
          void expoZoneSection.offsetWidth;

          // Esperar un poco más para que el contenido se adapte completamente
          await new Promise(resolve => setTimeout(resolve, 200));

          // Obtener el nuevo ancho después de que el contenido se haya adaptado
          const newWidth = expoZoneSection.offsetWidth || expoZoneSection.scrollWidth;

          // Crear un contenedor temporal que incluya el mapa, la leyenda y los contenedores de stands
          // Forzar ancho mínimo de desktop (1400px) para garantizar tamaño desktop siempre
          const desktopWidthForCapture = Math.max(1400, newWidth);
          tempContainer = document.createElement('div');
          tempContainer.style.cssText = `
            position: absolute;
            left: -9999px;
            top: 0;
            width: ${desktopWidthForCapture}px;
            min-width: 1400px;
            max-width: none;
            display: flex;
            flex-direction: column;
            gap: 0;
            box-sizing: border-box;
          `;

          // Clonar el mapa (.stands-section)
          const standsSection = expoZoneSection.querySelector('.stands-section') as HTMLElement;
          if (standsSection) {
            const clonedMap = standsSection.cloneNode(true) as HTMLElement;
            tempContainer.appendChild(clonedMap);

          }

          // Clonar la leyenda (.stands-legend-container)
          const legendContainer = expoZoneSection.querySelector('.stands-legend-container') as HTMLElement;
          if (legendContainer) {
            const clonedLegend = legendContainer.cloneNode(true) as HTMLElement;
            tempContainer.appendChild(clonedLegend);

          }

          // Clonar el contenedor de stands seleccionados y confirmados (.stands-column-container)
          const standsColumnContainer = expoZoneSection.querySelector('.stands-column-container') as HTMLElement;
          if (standsColumnContainer) {
            const clonedStandsContainer = standsColumnContainer.cloneNode(true) as HTMLElement;
            tempContainer.appendChild(clonedStandsContainer);

          }

          // Si tenemos al menos el mapa, usar el contenedor temporal
          if (standsSection) {
            document.body.appendChild(tempContainer);
            expoZoneElement = tempContainer;

          }
        }

        // Estrategia alternativa: Si no se encuentra .expo-zone-section, buscar elementos individuales
        if (!expoZoneElement) {
          // Cerrar el sidebar durante la captura (si no se cerró antes)
          if (sidebarElement && !sidebarElement.classList.contains('closed')) {
            sidebarElement.classList.remove('open');
            sidebarElement.classList.add('closed');

          }

          // Actualizar la clase del form-content-area para que el contenido se adapte
          const formContentArea = document.querySelector('.form-content-area') as HTMLElement;
          if (formContentArea) {
            formContentArea.classList.remove('sidebar-open');
            formContentArea.classList.add('sidebar-closed');

          }

          // Esperar a que el sidebar se cierre y el layout se recalcule
          await new Promise(resolve => setTimeout(resolve, 300));

          // Esperar un poco más para que el contenido se adapte completamente
          await new Promise(resolve => setTimeout(resolve, 200));

          const standsSection = document.querySelector('.stands-section') as HTMLElement;

          // Forzar un reflow después de obtener la referencia
          if (standsSection) {
            void standsSection.offsetWidth;
          }
          const legendContainer = document.querySelector('.stands-legend-container') as HTMLElement;
          const standsColumnContainer = document.querySelector('.stands-column-container') as HTMLElement;

          if (standsSection || legendContainer || standsColumnContainer) {
            // Forzar ancho mínimo de desktop (1400px) para garantizar tamaño desktop siempre
            tempContainer = document.createElement('div');
            tempContainer.style.cssText = `
              position: absolute;
              left: -9999px;
              top: 0;
              width: 1400px;
              min-width: 1400px;
              max-width: none;
              display: flex;
              flex-direction: column;
              gap: 0;
              box-sizing: border-box;
            `;

            if (standsSection) {
              const clonedMap = standsSection.cloneNode(true) as HTMLElement;
              tempContainer.appendChild(clonedMap);
            }

            if (legendContainer) {
              const clonedLegend = legendContainer.cloneNode(true) as HTMLElement;
              tempContainer.appendChild(clonedLegend);
            }

            if (standsColumnContainer) {
              const clonedStandsContainer = standsColumnContainer.cloneNode(true) as HTMLElement;
              tempContainer.appendChild(clonedStandsContainer);

            }

            if (tempContainer.children.length > 0) {
              document.body.appendChild(tempContainer);
              expoZoneElement = tempContainer;

            }
          }
        }

        // Estrategia de fallback: buscar solo el mapa si no encontramos nada
        if (!expoZoneElement) {
          const standsMapScroll = document.querySelector('.stands-map-scroll') as HTMLElement;
          if (standsMapScroll) {
            expoZoneElement = standsMapScroll;

          }
        }

        // Estrategia 6: Buscar por otros selectores comunes (solo si no encontramos nada)
        if (!expoZoneElement) {
          const possibleSelectors = [
            '.stands-map',
            '[id*="stand"]'
          ];
          for (const selector of possibleSelectors) {
            const element = document.querySelector(selector) as HTMLElement;
            if (element && element.offsetWidth > 100 && element.offsetHeight > 100) {
              expoZoneElement = element;

              break;
            }
          }
        }

        if (!expoZoneElement) {

          // Si no se encuentra, agregar información textual
          addSectionTitle('Zona Expo');
          if (profileData.selectedStands && Array.isArray(profileData.selectedStands) && profileData.selectedStands.length > 0) {
            addText('Stands Seleccionados:', 11, true);
            profileData.selectedStands.forEach(stand => {
              addText(`- Stand ${stand}`, 10);
            });
          }
          if (profileData.confirmedStands && Array.isArray(profileData.confirmedStands) && profileData.confirmedStands.length > 0) {
            addText('Stands Confirmados:', 11, true);
            profileData.confirmedStands.forEach(stand => {
              addText(`- Stand ${stand}`, 10);
            });
          }

          // Restaurar estado del sidebar antes de retornar
          if (sidebarElement) {
            if (wasSidebarOpen) {
              sidebarElement.classList.remove('closed');
              sidebarElement.classList.add('open');
            } else {
              sidebarElement.classList.remove('open');
              sidebarElement.classList.add('closed');
            }
          }

          // Restaurar clase del form-content-area
          const formContentArea = document.querySelector('.form-content-area') as HTMLElement;
          if (formContentArea) {
            if (wasSidebarOpen) {
              formContentArea.classList.remove('sidebar-closed');
              formContentArea.classList.add('sidebar-open');
            } else {
              formContentArea.classList.remove('sidebar-open');
              formContentArea.classList.add('sidebar-closed');
            }
          }

          // Restaurar pestañas originales antes de retornar
          if (savedSubTabText && activeSubTabButton) {
            activeSubTabButton.click();
            await new Promise(resolve => setTimeout(resolve, 200));
          }
          if (savedMainTabText && activeMainTabButton) {
            activeMainTabButton.click();
            await new Promise(resolve => setTimeout(resolve, 200));
          }

          return false; // Indica que no se encontró el elemento
        }

        // Asegurar que el sidebar esté cerrado antes de la captura (si no se cerró antes)
        if (sidebarElement && !sidebarElement.classList.contains('closed')) {
          sidebarElement.classList.remove('open');
          sidebarElement.classList.add('closed');

          // Actualizar la clase del form-content-area para que el contenido se adapte
          const formContentArea = document.querySelector('.form-content-area') as HTMLElement;
          if (formContentArea) {
            formContentArea.classList.remove('sidebar-open');
            formContentArea.classList.add('sidebar-closed');

          }

          // Esperar a que el sidebar se cierre y el layout se recalcule
          await new Promise(resolve => setTimeout(resolve, 300));

          // Forzar un reflow para asegurar que el layout se haya recalculado
          if (expoZoneElement) {
            void expoZoneElement.offsetWidth;
          }

          // Esperar un poco más para que el contenido se adapte completamente
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Verificar que el elemento tenga dimensiones válidas
        const elementRect = expoZoneElement.getBoundingClientRect();
        if (elementRect.width === 0 || elementRect.height === 0) {

          // Limpiar contenedor temporal si existe
          if (tempContainer && tempContainer.parentNode) {
            tempContainer.parentNode.removeChild(tempContainer);
          }
          // Si no tiene dimensiones, agregar información textual
          addSectionTitle('Zona Expo');
          if (profileData.selectedStands && Array.isArray(profileData.selectedStands) && profileData.selectedStands.length > 0) {
            addText('Stands Seleccionados:', 11, true);
            profileData.selectedStands.forEach(stand => {
              addText(`- Stand ${stand}`, 10);
            });
          }
          if (profileData.confirmedStands && Array.isArray(profileData.confirmedStands) && profileData.confirmedStands.length > 0) {
            addText('Stands Confirmados:', 11, true);
            profileData.confirmedStands.forEach(stand => {
              addText(`- Stand ${stand}`, 10);
            });
          }

          // Restaurar pestañas originales antes de retornar
          if (savedSubTabText && activeSubTabButton) {
            activeSubTabButton.click();
            await new Promise(resolve => setTimeout(resolve, 200));
          }
          if (savedMainTabText && activeMainTabButton) {
            activeMainTabButton.click();
            await new Promise(resolve => setTimeout(resolve, 200));
          }

          return false;
        }

        // Guardar estilos originales
        const originalStyles = {
          display: expoZoneElement.style.display,
          visibility: expoZoneElement.style.visibility,
          position: expoZoneElement.style.position,
          opacity: expoZoneElement.style.opacity,
          zIndex: expoZoneElement.style.zIndex,
          transform: expoZoneElement.style.transform
        };

        // Asegurar que el elemento esté visible antes de capturarlo
        expoZoneElement.style.display = 'block';
        expoZoneElement.style.visibility = 'visible';
        expoZoneElement.style.position = 'relative';
        expoZoneElement.style.opacity = '1';
        expoZoneElement.style.zIndex = '9999';
        expoZoneElement.style.transform = 'none';

        // También asegurar que los elementos padre estén visibles
        let parent = expoZoneElement.parentElement;
        while (parent && parent !== document.body) {
          const parentStyles = window.getComputedStyle(parent);
          if (parentStyles.display === 'none') {
            parent.style.display = 'block';
          }
          if (parentStyles.visibility === 'hidden') {
            parent.style.visibility = 'visible';
          }
          parent = parent.parentElement;
        }

        // Guardar tema actual y forzar tema light
        const htmlElement = document.documentElement;
        const originalThemeClasses = {
          hasDarkTheme: htmlElement.classList.contains('dark-theme'),
          hasLightTheme: htmlElement.classList.contains('light-theme')
        };

        // Forzar tema light
        htmlElement.classList.remove('dark-theme');
        htmlElement.classList.add('light-theme');

        // Guardar estilos originales del body y html para forzar vista desktop
        const bodyElement = document.body;
        const originalBodyStyles = {
          minWidth: bodyElement.style.minWidth,
          width: bodyElement.style.width,
          maxWidth: bodyElement.style.maxWidth
        };

        const originalHtmlStyles = {
          minWidth: htmlElement.style.minWidth,
          width: htmlElement.style.width
        };

        // Forzar ancho mínimo de desktop en html y body para que los elementos se rendericen como desktop
        // Usar un ancho fijo grande para garantizar que siempre sea desktop
        const desktopMinWidth = '1400px'; // Ancho mínimo garantizado para desktop
        htmlElement.style.minWidth = desktopMinWidth;
        htmlElement.style.width = desktopMinWidth;
        bodyElement.style.minWidth = desktopMinWidth;
        bodyElement.style.width = desktopMinWidth;
        bodyElement.style.maxWidth = 'none';

        // Forzar también en el contenedor temporal si existe
        if (tempContainer) {
          tempContainer.style.minWidth = desktopMinWidth;
          tempContainer.style.width = desktopMinWidth;
        }

        // Forzar estilos en el elemento expoZoneElement
        if (expoZoneElement) {
          expoZoneElement.style.minWidth = desktopMinWidth;
          expoZoneElement.style.width = '100%';
          expoZoneElement.style.maxWidth = 'none';
        }

        // Guardar y modificar viewport meta tag para forzar vista desktop
        const viewportMeta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
        const originalViewportContent = viewportMeta?.content || '';

        // Forzar viewport desktop con ancho fijo grande
        if (viewportMeta) {
          viewportMeta.content = 'width=1400, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
        }

        // Calcular dimensiones desktop (usar mínimo de 1400px para garantizar desktop)
        const elementWidth = Math.max(1400, expoZoneElement.scrollWidth || expoZoneElement.offsetWidth || 1400);
        const elementHeight = expoZoneElement.scrollHeight || expoZoneElement.offsetHeight || 768;
        const desktopWidth = elementWidth;
        const desktopHeight = elementHeight;

        // Esperar más tiempo para que el tema, viewport y estilos se apliquen completamente
        // Especialmente importante en dispositivos móviles
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Forzar un reflow completo después de aplicar todos los estilos
        void htmlElement.offsetWidth;
        void bodyElement.offsetWidth;
        if (expoZoneElement) {
          void expoZoneElement.offsetWidth;
        }

        // Esperar un poco más para que el navegador procese los cambios
        await new Promise(resolve => setTimeout(resolve, 500));

        // Forzar un reflow para asegurar que los cambios se apliquen
        void expoZoneElement.offsetHeight;

        // Reemplazar URLs de imágenes de WordPress con URLs del proxy para evitar CORS
        const wordpressDomain = 'smartcityexposantiagodelestero.com';
        let proxyBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;
        proxyBaseUrl = proxyBaseUrl.replace(/\/api\/?$/, '');
        const images = expoZoneElement.querySelectorAll('img');
        const originalSrcs: Map<HTMLImageElement, string> = new Map();

        images.forEach((img) => {
          const src = img.getAttribute('src');
          if (src && src.includes(wordpressDomain)) {
            originalSrcs.set(img, src);
            const proxyUrl = `${proxyBaseUrl}/api/proxy-image?url=${encodeURIComponent(src)}`;
            img.setAttribute('src', proxyUrl);

          }
        });

        // Esperar a que las imágenes se carguen a través del proxy
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Verificar dimensiones después de hacer visible
        // Recalcular dimensiones después de aplicar estilos
        // Forzar que el ancho sea al menos 1400px para garantizar desktop
        const finalWidth = Math.max(1400, expoZoneElement.scrollWidth || expoZoneElement.offsetWidth || desktopWidth);
        const finalHeight = expoZoneElement.scrollHeight || expoZoneElement.offsetHeight || desktopHeight;

        // Verificar que las dimensiones sean válidas
        if (finalWidth === 0 || finalHeight === 0) {

          throw new Error('Dimensiones inválidas para captura');
        }

        let canvas: HTMLCanvasElement;
        try {

          // Capturar como imagen con mejor calidad, tema light y viewport desktop
          canvas = await html2canvas(expoZoneElement as HTMLElement, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            allowTaint: false, // Cambiar a false para usar el proxy
            width: finalWidth,
            height: finalHeight
          });

          // Verificar que el canvas se generó correctamente
          if (!canvas || canvas.width === 0 || canvas.height === 0) {

            throw new Error('Canvas inválido');
          }

        } catch (captureError) {

          // Si falla la captura, restaurar todo y mostrar listado textual
          bodyElement.style.minWidth = originalBodyStyles.minWidth;
          bodyElement.style.width = originalBodyStyles.width;
          bodyElement.style.maxWidth = originalBodyStyles.maxWidth || '';
          htmlElement.style.minWidth = originalHtmlStyles.minWidth;
          htmlElement.style.width = originalHtmlStyles.width;
          if (viewportMeta && originalViewportContent) {
            viewportMeta.content = originalViewportContent;
          }
          if (originalThemeClasses.hasDarkTheme) {
            htmlElement.classList.add('dark-theme');
          }
          if (!originalThemeClasses.hasLightTheme) {
            htmlElement.classList.remove('light-theme');
          }
          // Restaurar URLs originales de las imágenes
          originalSrcs.forEach((originalSrc, img) => {
            img.setAttribute('src', originalSrc);
          });

          expoZoneElement.style.display = originalStyles.display;
          expoZoneElement.style.visibility = originalStyles.visibility;
          expoZoneElement.style.position = originalStyles.position;
          expoZoneElement.style.opacity = originalStyles.opacity;
          expoZoneElement.style.zIndex = originalStyles.zIndex;
          expoZoneElement.style.transform = originalStyles.transform;

          // Mostrar listado textual como fallback
          addSectionTitle('Zona Expo');
          if (profileData.selectedStands && Array.isArray(profileData.selectedStands) && profileData.selectedStands.length > 0) {
            addText('Stands Seleccionados:', 11, true);
            profileData.selectedStands.forEach(stand => {
              addText(`- Stand ${stand}`, 10);
            });
          }
          if (profileData.confirmedStands && Array.isArray(profileData.confirmedStands) && profileData.confirmedStands.length > 0) {
            addText('Stands Confirmados:', 11, true);
            profileData.confirmedStands.forEach(stand => {
              addText(`- Stand ${stand}`, 10);
            });
          }

          // Restaurar estado del sidebar antes de retornar
          if (sidebarElement) {
            if (wasSidebarOpen) {
              sidebarElement.classList.remove('closed');
              sidebarElement.classList.add('open');
            } else {
              sidebarElement.classList.remove('open');
              sidebarElement.classList.add('closed');
            }
          }

          // Restaurar clase del form-content-area
          const formContentArea = document.querySelector('.form-content-area') as HTMLElement;
          if (formContentArea) {
            if (wasSidebarOpen) {
              formContentArea.classList.remove('sidebar-closed');
              formContentArea.classList.add('sidebar-open');
            } else {
              formContentArea.classList.remove('sidebar-open');
              formContentArea.classList.add('sidebar-closed');
            }
          }

          // Restaurar pestañas originales
          if (savedSubTabText && activeSubTabButton) {
            activeSubTabButton.click();
            await new Promise(resolve => setTimeout(resolve, 200));
          }
          if (savedMainTabText && activeMainTabButton) {
            activeMainTabButton.click();
            await new Promise(resolve => setTimeout(resolve, 200));
          }

          return false;
        }

        // Restaurar estilos del body y html
        bodyElement.style.minWidth = originalBodyStyles.minWidth;
        bodyElement.style.width = originalBodyStyles.width;
        bodyElement.style.maxWidth = originalBodyStyles.maxWidth || '';
        htmlElement.style.minWidth = originalHtmlStyles.minWidth;
        htmlElement.style.width = originalHtmlStyles.width;

        // Restaurar viewport original
        if (viewportMeta && originalViewportContent) {
          viewportMeta.content = originalViewportContent;
        }

        // Restaurar tema original
        if (originalThemeClasses.hasDarkTheme) {
          htmlElement.classList.add('dark-theme');
        }
        if (!originalThemeClasses.hasLightTheme) {
          htmlElement.classList.remove('light-theme');
        }

        // Restaurar URLs originales de las imágenes
        originalSrcs.forEach((originalSrc, img) => {
          img.setAttribute('src', originalSrc);
        });

        // Restaurar estilos originales (solo si no es un contenedor temporal)
        if (!tempContainer) {
          expoZoneElement.style.display = originalStyles.display;
          expoZoneElement.style.visibility = originalStyles.visibility;
          expoZoneElement.style.position = originalStyles.position;
          expoZoneElement.style.opacity = originalStyles.opacity;
          expoZoneElement.style.zIndex = originalStyles.zIndex;
          expoZoneElement.style.transform = originalStyles.transform;
        }

        const imgData = canvas.toDataURL('image/png', 1.0);
        const imgWidth = pageWidth - (margin * 2);
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // El mapa siempre debe estar en una página nueva (ya se creó antes de llamar a captureExpoZone)
        // Asegurar que estamos al inicio de la página
        yPosition = margin;

        addSectionTitle('Zona Expo - Mapa de Stands');
        doc.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight + 5;

        // Crear nueva página después del mapa para que no haya otra sección en la misma página
        doc.addPage();
        yPosition = margin;

        // NO mostrar listado textual si se capturó visualmente exitosamente
        // (solo se muestra si falla la captura)

        // Limpiar contenedor temporal si existe
        if (tempContainer && tempContainer.parentNode) {
          tempContainer.parentNode.removeChild(tempContainer);

        }

        // Restaurar estado del sidebar después de la captura
        if (sidebarElement) {
          if (wasSidebarOpen) {
            sidebarElement.classList.remove('closed');
            sidebarElement.classList.add('open');

          } else {
            sidebarElement.classList.remove('open');
            sidebarElement.classList.add('closed');

          }
        }

        // Restaurar clase del form-content-area
        const formContentArea = document.querySelector('.form-content-area') as HTMLElement;
        if (formContentArea) {
          if (wasSidebarOpen) {
            formContentArea.classList.remove('sidebar-closed');
            formContentArea.classList.add('sidebar-open');

          } else {
            formContentArea.classList.remove('sidebar-open');
            formContentArea.classList.add('sidebar-closed');

          }
        }

        // Restaurar pestañas originales
        if (savedSubTabText && activeSubTabButton) {
          activeSubTabButton.click();
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        if (savedMainTabText && activeMainTabButton) {
          activeMainTabButton.click();
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        return true; // Indica que se capturó exitosamente
      } catch (error) {
        // Limpiar contenedor temporal si existe (incluso si falla)
        if (tempContainer && tempContainer.parentNode) {
          tempContainer.parentNode.removeChild(tempContainer);

        }

        // Restaurar estado del sidebar incluso si falla
        if (sidebarElement) {
          if (wasSidebarOpen) {
            sidebarElement.classList.remove('closed');
            sidebarElement.classList.add('open');

          } else {
            sidebarElement.classList.remove('open');
            sidebarElement.classList.add('closed');

          }
        }

        // Restaurar clase del form-content-area
        const formContentArea = document.querySelector('.form-content-area') as HTMLElement;
        if (formContentArea) {
          if (wasSidebarOpen) {
            formContentArea.classList.remove('sidebar-closed');
            formContentArea.classList.add('sidebar-open');

          } else {
            formContentArea.classList.remove('sidebar-open');
            formContentArea.classList.add('sidebar-closed');

          }
        }

        // Restaurar pestañas originales incluso si falla
        if (savedSubTabText && activeSubTabButton) {
          activeSubTabButton.click();
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        if (savedMainTabText && activeMainTabButton) {
          activeMainTabButton.click();
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Si falla, agregar información textual
        addSectionTitle('Zona Expo');
        if (profileData.selectedStands && Array.isArray(profileData.selectedStands) && profileData.selectedStands.length > 0) {
          addText('Stands Seleccionados:', 11, true);
          profileData.selectedStands.forEach(stand => {
            addText(`- Stand ${stand}`, 10);
          });
        }
        if (profileData.confirmedStands && Array.isArray(profileData.confirmedStands) && profileData.confirmedStands.length > 0) {
          addText('Stands Confirmados:', 11, true);
          profileData.confirmedStands.forEach(stand => {
            addText(`- Stand ${stand}`, 10);
          });
        }

        return false; // Indica que falló la captura
      }
    };

    // Zona Expo (captura visual) - Siempre crear página nueva para el mapa
    if ((profileData.selectedStands && Array.isArray(profileData.selectedStands) && profileData.selectedStands.length > 0) ||
        (profileData.confirmedStands && Array.isArray(profileData.confirmedStands) && profileData.confirmedStands.length > 0)) {
      // Siempre crear página nueva antes del mapa
      doc.addPage();
      yPosition = margin;
      await captureExpoZone();
    }

    // Función helper para agregar foto de orador
    const addSpeakerPhoto = async (photoUrl: string | undefined, photoSize: number = 30): Promise<void> => {
      if (!photoUrl || typeof photoUrl !== 'string') return;

      try {
        // Verificar si es una URL válida (http/https) o data URL
        const isValidUrl = photoUrl.startsWith('http://') || 
                          photoUrl.startsWith('https://') || 
                          photoUrl.startsWith('data:image/');

        if (!isValidUrl) return;

        // Verificar si hay espacio en la página actual
        if (yPosition + photoSize + 5 > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }

        // Usar proxy si la URL es de WordPress
        const imageUrl = getProxyUrl(photoUrl);

        // Cargar y agregar la imagen
        const img = new Image();
        img.crossOrigin = 'anonymous';

        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            try {
              // Calcular dimensiones manteniendo aspect ratio
              const aspectRatio = img.width / img.height;
              const imgWidth = photoSize * aspectRatio;
              const imgHeight = photoSize;

              // Agregar la imagen al PDF usando la URL del proxy
              doc.addImage(imageUrl, 'PNG', margin, yPosition, imgWidth, imgHeight);
              yPosition += imgHeight + 8; // Aumentar espacio después de la foto
              resolve();
            } catch (error) {

              resolve(); // Resolver de todas formas para no bloquear
            }
          };
          img.onerror = (error) => {

            resolve(); // Resolver de todas formas para no bloquear
          };
          img.src = imageUrl;
        });
      } catch (error) {
      }
    };

    if (onProgress) onProgress(50);

    // Función helper para convertir imagen a data URL (reutilizable)
    const imageToDataURL = async (imageUrl: string): Promise<string | null> => {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
              resolve(null);
              return;
            }

            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/png');
            resolve(dataURL);
          } catch (error) {

            resolve(null);
          }
        };

        img.onerror = () => {

          resolve(null);
        };

        img.src = imageUrl;
      });
    };

    // Función helper para mostrar orador (hasta Minibiografía, con foto y logo)
    const addSpeakerToPDF = async (speaker: any, fontSize: number = 10, indent: string = '') => {
      const fullName = speaker.firstName || speaker.lastName 
        ? `${speaker.firstName || ''} ${speaker.lastName || ''}`.trim()
        : '';

      if (!fullName) return; // No mostrar si no tiene nombre

      // Calcular posición X del nombre para alinear imágenes (especialmente para eventos paralelos)
      let nameStartX = margin;
      if (indent) {
        // Calcular el ancho del indent (espacios) antes de renderizar
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(fontSize + 1);
        const indentWidth = doc.getTextWidth(indent);
        nameStartX = margin + indentWidth;
      }

      // Mostrar nombre como título
      addText(`${indent}${fullName}`, fontSize + 1, true);

      // Agregar foto y logo lado a lado si existen
      const hasPhoto = speaker.photo && typeof speaker.photo === 'string';
      const hasLogo = speaker.organizationLogo && typeof speaker.organizationLogo === 'string';

      if (hasPhoto || hasLogo) {
        // Altura fija para todas las imágenes (foto y logo) - alineación vertical
        const fixedImageHeight = 25;
        // Agregar margen superior adicional antes de las imágenes
        yPosition += 3; // Margen superior reducido para las imágenes
        if (yPosition + fixedImageHeight + 5 > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }

        // Alinear imágenes al inicio del nombre (no al margen)
        const startX = nameStartX;
        let currentX = startX;
        const spacing = 5;
        // Usar la posición Y actual (después del nombre y el margen superior)
        const imagesY = yPosition;

          // Agregar foto del orador si existe
        if (hasPhoto && typeof speaker.photo === 'string') {
          try {
            const imageUrl = getProxyUrl(speaker.photo);
            const dataURL = await imageToDataURL(imageUrl);

            if (dataURL) {
              const img = new Image();
              await new Promise<void>((resolve) => {
                img.onload = () => {
                  try {
                    const aspectRatio = img.width / img.height;
                    const imgWidth = fixedImageHeight * aspectRatio;
                    const imgHeight = fixedImageHeight;

                    doc.addImage(dataURL, 'PNG', currentX, imagesY, imgWidth, imgHeight);
                    currentX += imgWidth + spacing;
                    resolve();
                  } catch (error) {

                    resolve();
                  }
                };
                img.onerror = () => resolve();
                img.src = dataURL;
              });
            }
          } catch (error) {

          }
        }

        // Agregar logo de la organización al lado si existe
        if (hasLogo && typeof speaker.organizationLogo === 'string') {
          try {
            const logoUrl = getProxyUrl(speaker.organizationLogo);
            const dataURL = await imageToDataURL(logoUrl);

            if (dataURL) {
              const img = new Image();
              await new Promise<void>((resolve) => {
                img.onload = () => {
                  try {
                    const aspectRatio = img.width / img.height;
                    const imgWidth = fixedImageHeight * aspectRatio;
                    const imgHeight = fixedImageHeight;

                    // Usar la misma posición Y para alineación vertical
                    doc.addImage(dataURL, 'PNG', currentX, imagesY, imgWidth, imgHeight);
                    resolve();
                  } catch (error) {

                    resolve();
                  }
                };
                img.onerror = () => resolve();
                img.src = dataURL;
              });
            }
          } catch (error) {

          }
        }

        // Actualizar yPosition después de las imágenes
        yPosition += fixedImageHeight + 8;
      }

      // Mostrar campos solo hasta Minibiografía (sin duplicar el nombre que ya está como título)
          if (speaker.nationality?.trim()) {
        addText(`${indent}Nacionalidad: ${speaker.nationality}`, fontSize);
          }
          if (speaker.organization?.trim()) {
        addText(`${indent}Organización: ${speaker.organization}`, fontSize);
          }
          if (speaker.position?.trim()) {
        addText(`${indent}Cargo: ${speaker.position}`, fontSize);
          }
          if (speaker.biography?.trim()) {
        addText(`${indent}Biografía: ${speaker.biography}`, fontSize);
          }
          if (speaker.linkedin?.trim()) {
        addText(`${indent}LinkedIn: ${speaker.linkedin}`, fontSize);
          }
          if (speaker.instagram?.trim()) {
        addText(`${indent}Instagram: ${speaker.instagram}`, fontSize);
          }
          if (speaker.twitter?.trim()) {
        addText(`${indent}Twitter/X: ${speaker.twitter}`, fontSize);
          }
          if (speaker.description?.trim()) {
        addText(`${indent}Minibiografía: ${speaker.description}`, fontSize);
      }
      // NO mostrar thematicAxes ni contentDescription

      yPosition += 5; // Espaciado después de cada orador
    };

    // Oradores
    if (profileData.speakers && Array.isArray(profileData.speakers) && profileData.speakers.length > 0) {
      addSectionTitle('Oradores en Congreso');
      for (let index = 0; index < profileData.speakers.length; index++) {
        const speaker = profileData.speakers[index];
        const hasSpeakerInfo = (speaker.firstName || speaker.lastName) ||
          speaker.nationality?.trim() || speaker.organization?.trim() ||
          speaker.position?.trim() || speaker.biography?.trim() ||
          speaker.linkedin?.trim() || speaker.instagram?.trim() ||
          speaker.twitter?.trim() || speaker.description?.trim() ||
          (speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0) ||
          speaker.contentDescription?.trim() ||
          (speaker.photo && typeof speaker.photo === 'string') ||
          (speaker.organizationLogo && typeof speaker.organizationLogo === 'string');

        if (hasSpeakerInfo) {
          // Agregar línea roja separadora antes de cada orador (excepto el primero)
          if (index > 0) {
            if (yPosition + 2 > pageHeight - margin) {
              doc.addPage();
              yPosition = margin;
            }
            const titleRgb = hexToRgb('#CE2B15');
            doc.setDrawColor(titleRgb[0], titleRgb[1], titleRgb[2]);
            doc.setLineWidth(0.5);
            yPosition += 5; // Espacio antes de la línea
            doc.line(margin, yPosition, pageWidth - margin, yPosition);
            yPosition += 8; // Aumentar margen inferior para separar mejor el nombre del orador
          }

          // Usar función helper para mostrar orador (hasta Minibiografía)
          // La función ya muestra el nombre como título, no duplicar
          await addSpeakerToPDF(speaker, 10, '');

          // Mostrar campos adicionales solo para oradores de la pestaña principal (después de Minibiografía)
          // Reducir el espacio extra antes de agregar estos campos
          yPosition -= 5; // Compensar el espacio extra de addSpeakerToPDF (que agrega 5mm al final)
          if (speaker.thematicAxes && Array.isArray(speaker.thematicAxes) && speaker.thematicAxes.length > 0) {
            const thematicAxesLabels = speaker.thematicAxes.map(axis => getThematicAxisLabel(axis));
            addText(`Ejes Temáticos: ${thematicAxesLabels.join('; ')}`, 10);
          }
          if (speaker.contentDescription?.trim()) {
            addText(`Descripción del Contenido: ${speaker.contentDescription}`, 10);
          }
          // El espaciado final ya está incluido en el último addText
        }
      }
      yPosition += 5; // Margen adicional después de la sección de Oradores
    }

    if (onProgress) onProgress(60);

    // Eventos Paralelos
    if (profileData.parallelEvents && Array.isArray(profileData.parallelEvents) && profileData.parallelEvents.length > 0) {
      addSectionTitle('Eventos Paralelos');
      for (let index = 0; index < profileData.parallelEvents.length; index++) {
        // Agregar separador completo entre eventos (excepto el primero)
        if (index > 0) {
          if (yPosition + 2 > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
          const titleRgb = hexToRgb('#CE2B15');
          doc.setDrawColor(titleRgb[0], titleRgb[1], titleRgb[2]);
          doc.setLineWidth(0.5);
          yPosition += 5; // Espacio antes de la línea
          doc.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 8; // Espacio después de la línea (igual que en oradores principales)
        }

        const event = profileData.parallelEvents[index];
        const hasEventInfo = event.name?.trim() || event.description?.trim() ||
          event.duration?.trim() ||
          (event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0) ||
          (event.speakerIds && Array.isArray(event.speakerIds) && event.speakerIds.length > 0) ||
          (event.localSpeakers && Array.isArray(event.localSpeakers) && event.localSpeakers.length > 0) ||
          (event.attendeeProfiles && Array.isArray(event.attendeeProfiles) && event.attendeeProfiles.length > 0);

        if (hasEventInfo) {
          // Mostrar título directamente sin "Evento X:" ni label "Título:"
          if (event.name?.trim()) {
            addText(event.name, 11, true);
          }

          if (event.description?.trim()) {
            addText(`Descripción: ${event.description}`, 10);
          }
          if (event.duration?.trim()) {
            addText(`Duración: ${event.duration} minutos`, 10);
          }
          if (event.thematicAxes && Array.isArray(event.thematicAxes) && event.thematicAxes.length > 0) {
            const thematicAxesLabels = event.thematicAxes.map(axis => getThematicAxisLabel(axis));
            addText(`Ejes Temáticos: ${thematicAxesLabels.join('; ')}`, 10);
          }

          // Combinar oradores de speakerIds (pestaña principal) y localSpeakers
          const allSpeakers: any[] = [];

          // Agregar oradores de la pestaña principal (speakerIds)
          if (event.speakerIds && Array.isArray(event.speakerIds) && profileData.speakers && Array.isArray(profileData.speakers)) {
            const speakers = profileData.speakers;
            event.speakerIds.forEach(speakerId => {
              const speaker = speakers.find(s => s.id === speakerId);
              if (speaker) {
                allSpeakers.push(speaker);
              }
            });
          }

          // Agregar oradores locales
          if (event.localSpeakers && Array.isArray(event.localSpeakers)) {
            allSpeakers.push(...event.localSpeakers);
          }

          // Mostrar todos los oradores
          if (allSpeakers.length > 0) {
            addText('Oradores:', 10, true);
            for (let idx = 0; idx < allSpeakers.length; idx++) {
              const speaker = allSpeakers[idx];

              // Agregar separador corto entre oradores (excepto el primero), comenzando donde comienzan los campos
              if (idx > 0) {
                if (yPosition + 2 > pageHeight - margin) {
                  doc.addPage();
                  yPosition = margin;
                }
                const titleRgb = hexToRgb('#CE2B15');
                doc.setDrawColor(titleRgb[0], titleRgb[1], titleRgb[2]);
                doc.setLineWidth(0.5);
                yPosition += 3; // Espacio antes de la línea

                // Calcular el ancho del indent '  ' para que el separador comience donde comienzan los campos
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
                const indentWidth = doc.getTextWidth('  ');
                const separatorStartX = margin + indentWidth;

                // Línea corta (50% del ancho disponible) comenzando donde comienzan los campos
                const lineLength = (pageWidth - margin * 2) * 0.5;
                doc.line(separatorStartX, yPosition, separatorStartX + lineLength, yPosition);
                yPosition += 8; // Aumentar margen inferior para separar mejor del nombre
              }

              await addSpeakerToPDF(speaker, 9, '  ');
            }
          }

          // Agregar perfil de asistentes (mostrar solo los perfiles separados por punto y coma)
          if (event.attendeeProfiles && Array.isArray(event.attendeeProfiles) && event.attendeeProfiles.length > 0) {
            const profileNames = event.attendeeProfiles
              .map((profile: any) => {
                // El campo es 'profile', no 'name'
                if (typeof profile === 'string') {
                  return profile;
                }
                return profile.profile || '';
              })
              .filter((name: string) => name && name.trim() !== '');

            if (profileNames.length > 0) {
              addText(`Perfil de asistentes: ${profileNames.join('; ')}`, 10);
            }
          }

          yPosition += 3;
        }
      }
      yPosition += 5; // Margen adicional después de la sección de Eventos Paralelos
    }

    // ========== CATÁLOGO ==========

    // Verificar si hay secciones del catálogo o participantes en rondas de negocios
    const hasCatalogSections = profileData.catalogSections && Array.isArray(profileData.catalogSections) && profileData.catalogSections.length > 0;
    const hasBusinessRoundsParticipants = profileData.businessRoundsParticipants && Array.isArray(profileData.businessRoundsParticipants) && profileData.businessRoundsParticipants.length > 0;

    if (hasCatalogSections || hasBusinessRoundsParticipants) {
      addSectionTitle('Catálogo / Rondas de negocios');

      // Secciones del catálogo
      if (hasCatalogSections) {
      // Convertir secciones del catálogo a nombres legibles
      const catalogLabels = profileData.catalogSections
        .map(section => {
          // Puede ser un objeto con id/value o un string
          let sectionId: string;
          if (typeof section === 'object' && section !== null) {
            sectionId = (section as any).id || (section as any).value || String(section);
          } else {
            sectionId = String(section);
          }

          // Buscar el label correspondiente
          const sectionLabel = catalogSections.find(s => s.value === sectionId)?.label;
          if (sectionLabel) {
            return sectionLabel;
          }

          // Si no se encuentra, intentar buscar por el string completo
          return sectionId;
        })
        .filter(label => label && label.trim() !== ''); // Filtrar labels vacíos

      if (catalogLabels.length > 0) {
          addText(`Secciones del catálogo: ${catalogLabels.join('; ')}`, 10);
      } else {
        // Mostrar los valores originales como fallback
        const fallbackLabels = profileData.catalogSections.map(s => String(s)).join('; ');
          addText(`Secciones del catálogo: ${fallbackLabels}`, 10);
        }
        yPosition += 3;
      }

      // Iniciativas e intereses - Unificar todas las iniciativas
      // Función helper para obtener label del tipo de iniciativa
      const getInitiativeTypeLabel = (type: string): string => {
        const typeMap: Record<string, string> = {
          'solution': 'Solución',
          'challenge': 'Desafío',
          'program': 'Programa de Impacto',
          'policy': 'Política Pública'
        };
        return typeMap[type] || type || 'Solución';
      };

      // Función helper para obtener el orden de los tipos (para ordenar)
      const getInitiativeTypeOrder = (type: string): number => {
        const orderMap: Record<string, number> = {
          'solution': 1,
          'challenge': 2,
          'program': 3,
          'policy': 4
        };
        return orderMap[type] || orderMap[''] || 1;
      };

    const allInitiatives = profileData.initiatives || [];

      // Preparar todas las iniciativas con sus datos completos
      const allInitiativesForPDF = allInitiatives.map((init: any) => {
        const type = init.type || 'solution';
        return {
        ...init,
          type: type,
        solutionType: init.solutionType || '',
          technologiesUsed: init.technologiesUsed || [],
          organizationArea: init.organizationArea || '',
          // Campos de contacto
          noContact: init.noContact || false,
          doNotPublishContact: init.doNotPublishContact || false,
          contactFirstName: init.contactFirstName || '',
          contactLastName: init.contactLastName || '',
          contactPosition: init.contactPosition || '',
          contactEmail: init.contactEmail || '',
          contactPhone: init.contactPhone || ''
        };
      });

      // Ordenar: primero soluciones, luego desafíos, luego programas, luego políticas
      allInitiativesForPDF.sort((a: any, b: any) => {
        return getInitiativeTypeOrder(a.type) - getInitiativeTypeOrder(b.type);
      });

      if (allInitiativesForPDF.length > 0) {
        addText('Iniciativas e intereses:', 10, true);

        allInitiativesForPDF.forEach((initiative: any, index: number) => {
          const currentType = initiative.type || 'solution';
          const previousType = index > 0 ? (allInitiativesForPDF[index - 1] as any).type || 'solution' : null;
          const isDifferentType = previousType !== null && previousType !== currentType;

          // Separador largo entre diferentes tipos, corto entre mismo tipo (excepto el primero)
          if (index > 0) {
            if (yPosition + 2 > pageHeight - margin) {
              doc.addPage();
              yPosition = margin;
            }
            const titleRgb = hexToRgb('#CE2B15');
            doc.setDrawColor(titleRgb[0], titleRgb[1], titleRgb[2]);
            doc.setLineWidth(0.5);
            yPosition += 3; // Espacio antes de la línea

            // Calcular el ancho del indent '  ' para que el separador comience donde comienzan los campos
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            const indentWidth = doc.getTextWidth('  ');
            const separatorStartX = margin + indentWidth;

            if (isDifferentType) {
              // Separador largo (ancho completo) entre diferentes tipos
              doc.line(separatorStartX, yPosition, pageWidth - margin, yPosition);
              yPosition += 8; // Margen inferior para separador largo
            } else {
              // Separador corto (50% del ancho) entre mismo tipo
              const lineLength = (pageWidth - margin * 2) * 0.5;
              doc.line(separatorStartX, yPosition, separatorStartX + lineLength, yPosition);
              yPosition += 8; // Margen inferior para separador corto
            }
          }

          // Determinar qué campos mostrar según el tipo
          const hasInitiativeInfo = initiative.name?.trim() || initiative.shortDescription?.trim() || initiative.description?.trim() ||
            initiative.currentStatus?.trim() ||
            initiative.organizationArea?.trim() || initiative.territory?.trim() || initiative.population?.trim() ||
            initiative.results?.trim() || initiative.approach?.trim() ||
            initiative.alliances?.trim() ||
            (initiative.thematicAxes && Array.isArray(initiative.thematicAxes) && initiative.thematicAxes.length > 0) ||
            (initiative.contactFirstName?.trim() || initiative.contactLastName?.trim() ||
              initiative.contactPosition?.trim() || initiative.contactEmail?.trim() || initiative.contactPhone?.trim()) ||
            (currentType === 'solution' && (initiative.solutionType?.trim() || 
              (initiative.technologiesUsed && Array.isArray(initiative.technologiesUsed) && initiative.technologiesUsed.length > 0))) ||
            (currentType === 'challenge' && (initiative.expectedImpact?.trim() || 
              (initiative.specificNeeds && Array.isArray(initiative.specificNeeds) && initiative.specificNeeds.length > 0))) ||
            (currentType === 'program' && initiative.estimatedBeneficiaries?.trim()) ||
            (currentType === 'policy' && initiative.policyType?.trim());

          if (hasInitiativeInfo) {
            // Agregar tipo de iniciativa como primer campo
            const typeLabel = getInitiativeTypeLabel(currentType);
            addText(`  Tipo de iniciativa: ${typeLabel}`, 10);

            // Nombre
            if (initiative.name?.trim()) {
              addText(`  Nombre: ${initiative.name}`, 10);
            }
            if (initiative.shortDescription?.trim()) {
              addText(`  Descripción Breve: ${initiative.shortDescription}`, 10);
            }
            if (initiative.description?.trim()) {
              addText(`  Descripción Amplia: ${initiative.description}`, 10);
            }
            if (initiative.currentStatus?.trim()) {
              addText(`  Estado Actual: ${getCurrentStatusLabel(initiative.currentStatus)}`, 10);
            }

            // Campos específicos por tipo
            if (currentType === 'solution') {
              if (initiative.solutionType?.trim()) {
                addText(`  Tipo de Solución: ${initiative.solutionType}`, 10);
              }
              if (initiative.technologiesUsed && Array.isArray(initiative.technologiesUsed) && initiative.technologiesUsed.length > 0) {
                addText(`  Tecnologías Utilizadas: ${initiative.technologiesUsed.join('; ')}`, 10);
              }
            } else if (currentType === 'challenge') {
              if (initiative.expectedImpact?.trim()) {
                const getExpectedImpactLabel = (impact: string): string => {
                  const impactMap: Record<string, string> = {
                    'social': 'Social',
                    'ambiental': 'Ambiental',
                    'economico': 'Económico',
                    'tecnologico': 'Tecnológico',
                    'institucional': 'Institucional',
                    'urbano': 'Urbano',
                    'otro': 'Otro'
                  };
                  return impactMap[impact] || impact;
                };
                addText(`  Impacto Esperado: ${getExpectedImpactLabel(initiative.expectedImpact)}`, 10);
              }
              if (initiative.specificNeeds && Array.isArray(initiative.specificNeeds) && initiative.specificNeeds.length > 0) {
                addText(`  Necesidades Específicas: ${initiative.specificNeeds.join('; ')}`, 10);
              }
            } else if (currentType === 'program') {
              if (initiative.estimatedBeneficiaries?.trim()) {
                addText(`  Beneficiarios Estimados: ${getEstimatedBeneficiariesLabel(initiative.estimatedBeneficiaries)}`, 10);
              }
            } else if (currentType === 'policy') {
              if (initiative.policyType?.trim()) {
                addText(`  Tipo: ${getPolicyTypeLabel(initiative.policyType)}`, 10);
              }
            }

            // Campos comunes
            if (initiative.territory?.trim()) {
              addText(`  Ámbito/Territorio: ${initiative.territory}`, 10);
            }
            if (initiative.population?.trim()) {
              addText(`  Población/Actores Involucrados: ${initiative.population}`, 10);
            }
            if (initiative.results?.trim()) {
              addText(`  Resultados/Evidencias: ${initiative.results}`, 10);
            }
            if (initiative.approach?.trim()) {
              addText(`  Enfoque/Metodología: ${initiative.approach}`, 10);
            }
            if (initiative.alliances?.trim()) {
              addText(`  Alianzas/Colaboraciones: ${initiative.alliances}`, 10);
            }
            if (initiative.thematicAxes && Array.isArray(initiative.thematicAxes) && initiative.thematicAxes.length > 0) {
              const thematicAxesLabels = initiative.thematicAxes.map((axis: string) => getThematicAxisLabel(axis));
              addText(`  Ejes Temáticos: ${thematicAxesLabels.join('; ')}`, 10);
            }
            if (initiative.organizationArea?.trim()) {
              addText(`  Área de la organización: ${initiative.organizationArea}`, 10);
            }

            // Datos de contacto (mostrar siempre, incluso si está marcado como no publicar)
            if (!initiative.noContact) {
              const hasContactInfo = initiative.contactFirstName?.trim() || initiative.contactLastName?.trim() ||
                initiative.contactPosition?.trim() || initiative.contactEmail?.trim() || initiative.contactPhone?.trim();
              if (hasContactInfo) {
                const contactFullName = `${initiative.contactFirstName || ''} ${initiative.contactLastName || ''}`.trim();
                if (contactFullName) {
                  addText(`  Contacto: ${contactFullName}`, 10);
                }
                if (initiative.contactPosition?.trim()) {
                  addText(`    Cargo: ${initiative.contactPosition}`, 10);
                }
                if (initiative.contactEmail?.trim()) {
                  addText(`    Correo: ${initiative.contactEmail}`, 10);
                }
                if (initiative.contactPhone?.trim()) {
                  addText(`    Teléfono: ${initiative.contactPhone}`, 10);
                }
              }
          }
          yPosition += 3;
        }
      });
        yPosition += 5; // Margen adicional después de la sección de Iniciativas
      }

      // Participantes en Rondas de Negocios
      if (hasBusinessRoundsParticipants && profileData.businessRoundsParticipants) {
        addText('Participantes en Rondas de Negocios:', 10, true);
        for (let index = 0; index < profileData.businessRoundsParticipants.length; index++) {
          const participant = profileData.businessRoundsParticipants[index];
          const hasParticipantInfo = (participant.firstName || participant.lastName) ||
            participant.position?.trim() || participant.organization?.trim() ||
            participant.email?.trim() || participant.phone?.trim() ||
            (participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0) ||
            (participant.organizationLogo && typeof participant.organizationLogo === 'string');

          if (hasParticipantInfo) {
            // Agregar separador chico entre participantes (excepto el primero)
            if (index > 0) {
              if (yPosition + 2 > pageHeight - margin) {
                doc.addPage();
                yPosition = margin;
              }
              const titleRgb = hexToRgb('#CE2B15');
              doc.setDrawColor(titleRgb[0], titleRgb[1], titleRgb[2]);
              doc.setLineWidth(0.5);
              yPosition += 3; // Espacio antes de la línea

              // Calcular el ancho del indent '  ' para que el separador comience donde comienzan los campos
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(10);
              const indentWidth = doc.getTextWidth('  ');
              const separatorStartX = margin + indentWidth;

              // Línea corta (50% del ancho disponible) comenzando donde comienzan los campos
              const lineLength = (pageWidth - margin * 2) * 0.5;
              doc.line(separatorStartX, yPosition, separatorStartX + lineLength, yPosition);
              yPosition += 8; // Margen inferior para separar mejor del nombre
            }

            const fullName = `${participant.firstName || ''} ${participant.lastName || ''}`.trim();
            if (fullName) {
              addText(`  Participante ${index + 1}: ${fullName}`, 10, true);
            } else {
              addText(`  Participante ${index + 1}:`, 10, true);
            }

            // Calcular posición X donde comienzan los labels (con indent '  ')
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            const indentWidth = doc.getTextWidth('  ');
            const labelsStartX = margin + indentWidth;

            // Agregar logo de la organización si existe (alineado a los labels)
            const hasLogo = participant.organizationLogo && typeof participant.organizationLogo === 'string';
            if (hasLogo && typeof participant.organizationLogo === 'string') {
              try {
                const logoUrl = getProxyUrl(participant.organizationLogo);
                const dataURL = await imageToDataURL(logoUrl);

                if (dataURL) {
                  // Verificar si hay espacio en la página actual
                  const fixedImageHeight = 25; // Misma altura que en oradores
                  yPosition += 3; // Margen superior reducido para las imágenes
                  if (yPosition + fixedImageHeight + 5 > pageHeight - margin) {
                    doc.addPage();
                    yPosition = margin;
                  }

                  const img = new Image();
                  await new Promise<void>((resolve) => {
                    img.onload = () => {
                      try {
                        const aspectRatio = img.width / img.height;
                        const imgWidth = fixedImageHeight * aspectRatio;
                        const imgHeight = fixedImageHeight;

                        // Alinear logo a los labels (no al margen)
                        doc.addImage(dataURL, 'PNG', labelsStartX, yPosition, imgWidth, imgHeight);
                        yPosition += imgHeight + 8;
                        resolve();
                      } catch (error) {

                        resolve();
                      }
                    };
                    img.onerror = () => resolve();
                    img.src = dataURL;
                  });
                }
              } catch (error) {

              }
            }

            if (participant.position?.trim()) {
              addText(`  Cargo: ${participant.position}`, 10);
            }
            if (participant.organization?.trim()) {
              addText(`  Organización: ${participant.organization}`, 10);
            }
            if (participant.email?.trim()) {
              addText(`  Correo: ${participant.email}`, 10);
            }
            if (participant.phone?.trim()) {
              addText(`  Teléfono: ${participant.phone}`, 10);
            }
            if (participant.thematicAxes && Array.isArray(participant.thematicAxes) && participant.thematicAxes.length > 0) {
              const thematicAxesLabels = participant.thematicAxes.map(axis => getThematicAxisLabel(axis));
              addText(`  Ejes Temáticos: ${thematicAxesLabels.join('; ')}`, 10);
          }
          yPosition += 3;
        }
        }
      }
      yPosition += 5; // Margen adicional después de la sección de Catálogo
    }

    if (onProgress) onProgress(85);

    // Progreso antes de guardar
    if (onProgress) onProgress(95);

    // Generar nombre del archivo para DESCARGA: usar organizationName si existe, sino usar "perfil_institucional"
    let fileName;
    if (profileData.basicInfo?.organizationName?.trim()) {
      const orgName = profileData.basicInfo.organizationName
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();
      fileName = `${orgName}.pdf`;
    } else {
      fileName = 'perfil_institucional.pdf';
    }

    // Progreso final
    if (onProgress) onProgress(100);

    return doc;

  } catch (error) {
    throw error; // Re-lanzar para que App.tsx lo capture
  }
};

/**
 * Genera un PDF del perfil institucional y lo descarga
 */
// Detectar si es dispositivo móvil
const isMobileDevice = (): boolean => {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
         (window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
};

export const generateProfilePDF = async (
  profileData: ProfileData,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const generateStartTime = Date.now();
  try {
    // Track inicio de generación
    AnalyticsEvents.pdfGenerateStart();
    const doc = await buildProfilePDF(profileData, onProgress);
    
    // Generar nombre del archivo para DESCARGA: usar organizationName si existe, sino usar "perfil_institucional"
    let fileName;
    if (profileData.basicInfo?.organizationName?.trim()) {
      const orgName = profileData.basicInfo.organizationName
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();
      fileName = `${orgName}.pdf`;
    } else {
      fileName = 'perfil_institucional.pdf';
    }
    
    // En mobile, usar blob download en lugar de doc.save() que puede fallar
    if (isMobileDevice()) {
      const blob = doc.output('blob');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      doc.save(fileName);
    }
    
    // Track éxito de generación
    const generationTime = Date.now() - generateStartTime;
    AnalyticsEvents.pdfGenerateSuccess(generationTime);
  } catch (error) {
    // Track error de generación
    const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
    AnalyticsEvents.pdfGenerateError(errorMessage);
    throw error;
  }
};

/**
 * Genera un PDF del perfil institucional como Blob para compartir
 */
export const generateProfilePDFAsBlob = async (
  profileData: ProfileData,
  onProgress?: (progress: number) => void
): Promise<{ blob: Blob; fileName: string }> => {
  const generateStartTime = Date.now();
  try {
    // Track inicio de generación
    AnalyticsEvents.pdfGenerateStart();
    const doc = await buildProfilePDF(profileData, onProgress);
    const blob = doc.output('blob');
    
    // Generar nombre del archivo: usar organizationName si existe, sino usar "perfil_institucional"
    let fileName;
    if (profileData.basicInfo?.organizationName?.trim()) {
      const orgName = profileData.basicInfo.organizationName
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();
      fileName = `${orgName}.pdf`;
    } else {
      fileName = 'perfil_institucional.pdf';
    }
    
    // Track éxito de generación
    const generationTime = Date.now() - generateStartTime;
    AnalyticsEvents.pdfGenerateSuccess(generationTime);
    return { blob, fileName };
  } catch (error) {
    // Track error de generación
    const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
    AnalyticsEvents.pdfGenerateError(errorMessage);
    throw error;
  }
};
